import { useMemo } from "react";
import { useSiteContent } from "@/lib/siteContent";

/**
 * Aktions-Landing (`/aktion`) — Master-Switch-Hook.
 *
 * Wichtige Abgrenzung (nicht verwechseln!):
 *  - `aktion.enabled`      → steuert die Referenz-Sektion + Banner
 *                            auf der Startseite (/), Editor unter
 *                            `/admin/aktion`. Gelesen von `useAktion()`.
 *  - `aktion_page.enabled` → steuert diese separate Landing-Page unter
 *                            `/aktion`, editiert unter
 *                            `/admin/aktions-seite`. Gelesen von diesem
 *                            Hook.
 *
 * Beide sind komplett unabhängig — der AKTION-Header-Button (der auf
 * `/aktion` linkt) hängt an *diesem* Hook, damit der Button synchron zur
 * Sichtbarkeit der Landing ein/aus geht (nicht zur Referenz-Sektion auf
 * der Startseite).
 *
 * Speicher-Struktur:
 *  - CMS-Key: `aktion_page`
 *  - Value:   ein großes Config-Objekt (siehe `useAktionsSeite.ts`)
 *             mit einem zusätzlichen Feld `enabled: boolean`.
 *
 * `ready` gibt an, ob der initiale Fetch abgeschlossen ist — für den
 * Route-Guard wichtig, um während des Ladens **keinen** vorzeitigen
 * Redirect auszulösen (kein Flackern beim Reload).
 *
 * Default-Verhalten: `enabled=true` (Bestandsdaten ohne diesen Key sollen
 * sichtbar bleiben, Backwards-Compat).
 */
export type AktionPageMaster = {
    enabled: boolean;
};

const DEFAULTS: AktionPageMaster = {
    enabled: true,
};

export function useAktionPage(): AktionPageMaster & { ready: boolean } {
    const site = useSiteContent();
    // Wir lesen das komplette `aktion_page`-Objekt, extrahieren aber nur
    // das `enabled`-Feld — der Rest wird von `useAktionsSeite()` verwaltet.
    const cms = site.getObject<Partial<AktionPageMaster>>("aktion_page", {});
    const merged: AktionPageMaster = useMemo(() => ({
        ...DEFAULTS,
        // Force boolean coercion — CMS könnte string liefern; Backwards-Compat:
        // fehlender Key → enabled=true (bisherige Bestandsdaten ohne Feld).
        enabled: cms?.enabled !== undefined ? cms.enabled === true : DEFAULTS.enabled,
    }), [cms]);
    return { ...merged, ready: site.ready };
}
